/**
 * MSW handlers — run the app standalone against synthetic Oxbo-M + Oxbo-P data
 * with no live services. URLs match what mApi / pApi call (built from
 * VITE_OXBO_M_URL / VITE_OXBO_P_URL) and return the SAME snake_case wire shapes
 * as the real services, so flipping VITE_USE_MOCKS=false is a drop-in swap.
 *
 * Plan writes (create / version / status / supplements / dataset PUTs / publish)
 * mutate in-session copies so the authoring flow feels live.
 */
import { http, HttpResponse } from "msw";
import type {
  WellOut,
  PlanOut,
  PlanCreate,
  PlanStatus,
  SupplementOut,
  PlanDatasets,
  PlanTimelineOut,
  TimelineNode,
  ProgramaStep,
  HolePlanRow,
  CasingRow,
  AttributeRow,
  AfeLineRow,
  TrajectoryStationInput,
  TrajectoryStationOut,
  PublishPreviewOut,
  PublishResultOut,
} from "@api/types";
import {
  WELLS,
  WELL_BY_ID,
  EVENTS_BY_WELL_UWI,
  PLANS_BY_EVENT,
  PLAN_BY_ID,
  DATASETS_BY_PLAN,
  emptyDatasets,
  TIMELINE_BY_PLAN,
  emptyTimeline,
  SUPPLEMENTS_BY_PLAN,
  CATALOGS,
} from "./seed";
import { FIELD_UNITS, PROFILE_LIST, RESOLVED_PROFILES } from "./unitsSeed";

const M = import.meta.env.VITE_OXBO_M_URL ?? "https://m.dev.oxbo.energy";
const P = import.meta.env.VITE_OXBO_P_URL ?? "https://p.dev.oxbo.energy";

// ── Mutable session state (seeded from the static fixtures) ─────────────────
const plansByEvent: Record<string, PlanOut[]> = Object.fromEntries(
  Object.entries(PLANS_BY_EVENT).map(([k, v]) => [k, v.map((p) => ({ ...p }))]),
);
const planById: Record<string, PlanOut> = Object.fromEntries(
  Object.entries(PLAN_BY_ID).map(([k, v]) => [k, { ...v }]),
);
const datasetsByPlan: Record<string, PlanDatasets> = Object.fromEntries(
  Object.entries(DATASETS_BY_PLAN).map(([k, v]) => [k, structuredClone(v)]),
);
const supplementsByPlan: Record<string, SupplementOut[]> = Object.fromEntries(
  // Deep-clone so PATCHes to a supplement's approval trail don't mutate the
  // shared static seed (the `approvals` array would otherwise be by-reference).
  Object.entries(SUPPLEMENTS_BY_PLAN).map(([k, v]) => [k, structuredClone(v)]),
);
const timelineByPlan: Record<string, PlanTimelineOut> = Object.fromEntries(
  Object.entries(TIMELINE_BY_PLAN).map(([k, v]) => [k, structuredClone(v)]),
);

function getTimeline(planId: string): PlanTimelineOut {
  return timelineByPlan[planId] ?? (timelineByPlan[planId] = emptyTimeline(planId));
}

let seq = 1000;
const nextId = (prefix: string) => `${prefix}-${++seq}`;

function getDatasets(planId: string): PlanDatasets {
  return datasetsByPlan[planId] ?? (datasetsByPlan[planId] = emptyDatasets(planId));
}

/**
 * Minimal min-curvature-ish roll (mock; the real service computes exactly). Rolls
 * TVD + northing/easting from the average-inclination/average-azimuth vector over
 * each MD step, and DLS from the inclination change per 30 m — enough to populate
 * both the vertical-section and plan (N-E) views when the author saves an edit.
 */
function computeTrajectory(stations: TrajectoryStationInput[]): TrajectoryStationOut[] {
  const out: TrajectoryStationOut[] = [];
  const D2R = Math.PI / 180;
  let tvd = 0;
  let north = 0;
  let east = 0;
  let prev: TrajectoryStationInput | null = null;
  for (const s of stations) {
    if (prev && s.md > prev.md) {
      const dMd = s.md - prev.md;
      const avgInc = ((prev.inclination + s.inclination) / 2) * D2R;
      const avgAzi = ((prev.azimuth + s.azimuth) / 2) * D2R;
      const horiz = dMd * Math.sin(avgInc);
      tvd += dMd * Math.cos(avgInc);
      north += horiz * Math.cos(avgAzi);
      east += horiz * Math.sin(avgAzi);
    }
    const dls =
      prev && s.md > prev.md
        ? Math.abs(s.inclination - prev.inclination) / ((s.md - prev.md) / 30)
        : 0;
    out.push({
      ...s,
      tvd: Math.round(tvd * 10) / 10,
      northing: Math.round(north * 10) / 10,
      easting: Math.round(east * 10) / 10,
      dls: Math.round(dls * 100) / 100,
    });
    prev = s;
  }
  return out;
}

/**
 * Compute the publish-preview validation + diff from a plan's datasets — the
 * mock stand-in for the server-side validator. Runs the six checks named in the
 * §4 publish contract (required fields, TVD≤MD, monotonic depth, cost balance,
 * casing nesting, program sequence) and diffs the planned targets against the
 * last published version. The real service computes these authoritatively; this
 * reproduces the same wire shape so the FE is a drop-in swap.
 */
function buildPublishPreview(planId: string, p: PlanOut, ds: PlanDatasets): PublishPreviewOut {
  const issues: PublishPreviewOut["issues"] = [];

  // required_fields — every design dataset the event needs must be non-empty.
  const missing: string[] = [];
  if (ds.objetivo.length === 0) missing.push("objetivo");
  if (ds.programa.length === 0) missing.push("programa");
  if (ds.hole_plan.length === 0) missing.push("hole_plan");
  if (ds.casing.length === 0) missing.push("casing");
  if (ds.afe_lines.length === 0) missing.push("afe_lines");
  if (missing.length > 0) {
    issues.push({
      level: "error",
      code: "required_fields",
      message: `Faltan campos obligatorios en: ${missing.join(", ")}.`,
    });
  }

  // tvd_le_md — planned TVD must not exceed final MD (objetivo attributes).
  const objVal = (name: string): number | null => {
    const row = ds.objetivo.find((o) => o.name.toLowerCase().includes(name));
    if (!row) return null;
    const n = Number(String(row.value).replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : null;
  };
  const md = objVal("md");
  const tvd = objVal("tvd");
  if (md != null && tvd != null && tvd > md) {
    issues.push({
      level: "error",
      code: "tvd_le_md",
      message: `TVD (${tvd} m) supera MD (${md} m) en el objetivo.`,
    });
  }

  // monotonic_depth — programa depth_until must never retreat in sequence order.
  const steps = [...ds.programa].sort((a, b) => a.seq - b.seq);
  let prevDepth = -Infinity;
  let retreat = false;
  for (const s of steps) {
    const d = s.depth_until ?? s.depth_from;
    if (d != null) {
      if (d < prevDepth) retreat = true;
      prevDepth = Math.max(prevDepth, d);
    }
  }
  if (retreat) {
    issues.push({
      level: "error",
      code: "monotonic_depth",
      message: "La profundidad del programa retrocede entre fases.",
    });
  }

  // cost_balance — Σ afe_line budget vs the authorized total (±3% = warn).
  const sumBudget = ds.afe_lines.reduce((acc, l) => acc + (l.budget ?? 0), 0);
  const target = p.authorized_total ?? 0;
  if (target > 0 && sumBudget > 0) {
    const delta = (sumBudget - target) / target;
    if (Math.abs(delta) > 0.1) {
      issues.push({
        level: "error",
        code: "cost_balance",
        message: `Las líneas de costo (US$ ${Math.round(sumBudget).toLocaleString()}) difieren del objetivo (US$ ${Math.round(target).toLocaleString()}) en ${(delta * 100).toFixed(1)}%.`,
      });
    } else if (Math.abs(delta) > 0.005) {
      issues.push({
        level: "warn",
        code: "cost_balance",
        message: `Las líneas de costo suman US$ ${Math.round(sumBudget).toLocaleString()} vs. un objetivo de US$ ${Math.round(target).toLocaleString()} (${(delta * 100).toFixed(1)}%). Dentro de tolerancia — revísese.`,
      });
    }
  }

  // casing_nesting — every hole-plan section needs a casing string on its hole
  // size; a hole section with no matching casing hole_size = an invalid nest.
  const casingHoleSizes = new Set(ds.casing.map((c) => c.hole_size).filter((h) => h != null));
  const orphanHole = ds.hole_plan.find(
    (h) => h.hole_diameter != null && !casingHoleSizes.has(h.hole_diameter),
  );
  if (orphanHole) {
    issues.push({
      level: "error",
      code: "casing_nesting",
      message: `La sección ${orphanHole.section} (${orphanHole.hole_diameter}") del hole plan no tiene un string de casing que anide en ese hoyo.`,
    });
  }

  // program_sequence — seq must be a gap-free 1..N run.
  const seqs = steps.map((s) => s.seq);
  const gapFree = seqs.every((v, i) => v === i + 1);
  if (steps.length > 0 && !gapFree) {
    issues.push({
      level: "warn",
      code: "program_sequence",
      message: "La secuencia del programa tiene huecos o duplicados (no es 1..N).",
    });
  }

  // Diff vs the last published version of this event (mock: the highest-version
  // published plan on the same event, if any). Compares the planned targets.
  const priorPublished = (plansByEvent[p.event_id] ?? [])
    .filter((q) => q.published_at && q.id !== planId)
    .sort((a, b) => b.version - a.version)[0];
  const diff: PublishPreviewOut["diff"] = [];
  if (priorPublished) {
    diff.push({
      dataset: "afe",
      field: "authorized_total",
      before: priorPublished.authorized_total?.toLocaleString() ?? null,
      after: p.authorized_total?.toLocaleString() ?? null,
    });
    diff.push({
      dataset: "programa",
      field: "phases",
      before: null,
      after: String(ds.programa.length),
    });
  }

  return {
    plan_id: planId,
    ready: issues.every((i) => i.level !== "error"),
    issues,
    diff,
    prior_version: priorPublished?.version ?? null,
  };
}

function filterWells(url: URL): WellOut[] {
  const q = url.searchParams;
  const status = q.get("status") ?? "";
  const activeOnly = q.get("active_only") === "true";
  return WELLS.filter((w) => {
    if (status && w.status !== status) return false;
    if (activeOnly && !w.is_active) return false;
    return true;
  });
}

export const handlers = [
  // ─────────────────────────── Oxbo-M ───────────────────────────
  http.get(`${M}/v1/wells`, ({ request }) => HttpResponse.json(filterWells(new URL(request.url)))),
  http.get(`${M}/v1/wells/:id`, ({ params }) => {
    const well = WELL_BY_ID[params.id as string];
    return well ? HttpResponse.json(well) : new HttpResponse(null, { status: 404 });
  }),
  http.get(`${M}/v1/categories/:key/resolved`, ({ params }) => {
    const cat = CATALOGS[params.key as string];
    return cat
      ? HttpResponse.json(cat)
      : HttpResponse.json({ category_key: params.key, generation: "gen-0", entries: [] });
  }),

  // ── UOM reference data (Oxbo-M) ──
  http.get(`${M}/v1/uom/profiles`, () => HttpResponse.json(PROFILE_LIST)),
  http.get(`${M}/v1/uom/profiles/:key/resolved`, ({ params }) => {
    const prof = RESOLVED_PROFILES[params.key as string];
    return prof ? HttpResponse.json(prof) : new HttpResponse(null, { status: 404 });
  }),
  http.get(`${M}/v1/uom/org/effective`, () =>
    HttpResponse.json({ detail: "No default unit-system profile set" }, { status: 404 }),
  ),

  // ── Field-units registry (Oxbo-P) ──
  http.get(`${P}/v1/meta/field-units`, () => HttpResponse.json(FIELD_UNITS)),

  // ─────────────────────────── Oxbo-P: events ───────────────────
  http.get(`${P}/v1/wells/:uwi/events`, ({ params }) => {
    const uwi = decodeURIComponent(params.uwi as string);
    return HttpResponse.json(EVENTS_BY_WELL_UWI[uwi] ?? []);
  }),

  // ─────────────────────────── Oxbo-P: plan lifecycle ───────────
  http.get(`${P}/v1/events/:eventId/plans`, ({ params }) => {
    const list = plansByEvent[params.eventId as string] ?? [];
    return HttpResponse.json([...list].sort((a, b) => b.version - a.version));
  }),
  http.get(`${P}/v1/plans/:id`, ({ params }) => {
    const p = planById[params.id as string];
    return p ? HttpResponse.json(p) : new HttpResponse(null, { status: 404 });
  }),
  http.post(`${P}/v1/events/:eventId/plans`, async ({ params, request }) => {
    const eventId = params.eventId as string;
    const body = (await request.json()) as PlanCreate;
    const wellUwi =
      Object.values(EVENTS_BY_WELL_UWI)
        .flat()
        .find((ev) => ev.id === eventId)?.well_uwi ?? "—";
    const id = nextId("pl");
    const created: PlanOut = {
      id,
      event_id: eventId,
      well_uwi: wellUwi,
      event_code: body.event_code,
      version: 1,
      status: "draft",
      afe_no: null,
      authorized_total: null,
      currency: "USD",
      actor: "demo.planner",
      updated_at: new Date().toISOString(),
      summary: null,
      published_at: null,
    };
    // Optionally clone an existing plan's datasets to seed the draft.
    const cloneSource = body.clone_from_plan_id
      ? datasetsByPlan[body.clone_from_plan_id]
      : undefined;
    if (cloneSource) {
      const cloned = structuredClone(cloneSource);
      cloned.plan_id = id;
      datasetsByPlan[id] = cloned;
    }
    planById[id] = created;
    (plansByEvent[eventId] ??= []).push(created);
    return HttpResponse.json(created, { status: 201 });
  }),
  http.post(`${P}/v1/plans/:id/versions`, ({ params }) => {
    const prior = planById[params.id as string];
    if (!prior) return new HttpResponse(null, { status: 404 });
    const id = nextId("pl");
    const next: PlanOut = {
      ...prior,
      id,
      version: prior.version + 1,
      status: "draft",
      updated_at: new Date().toISOString(),
      published_at: null,
    };
    // A new version inherits the prior version's datasets.
    datasetsByPlan[id] = structuredClone(getDatasets(prior.id));
    datasetsByPlan[id].plan_id = id;
    planById[id] = next;
    (plansByEvent[prior.event_id] ??= []).push(next);
    return HttpResponse.json(next, { status: 201 });
  }),
  http.patch(`${P}/v1/plans/:id`, async ({ params, request }) => {
    const p = planById[params.id as string];
    if (!p) return new HttpResponse(null, { status: 404 });
    const body = (await request.json()) as { status?: PlanStatus };
    if (body.status) p.status = body.status;
    p.updated_at = new Date().toISOString();
    return HttpResponse.json(p);
  }),

  // ── Supplements (D24) ──
  http.get(`${P}/v1/plans/:id/supplements`, ({ params }) =>
    HttpResponse.json(supplementsByPlan[params.id as string] ?? []),
  ),
  http.post(`${P}/v1/plans/:id/supplements`, async ({ params, request }) => {
    const planId = params.id as string;
    const body = (await request.json()) as { amount: number; reason: string };
    const list = (supplementsByPlan[planId] ??= []);
    const created: SupplementOut = {
      id: nextId("sup"),
      plan_id: planId,
      seq: list.length + 1,
      amount: body.amount,
      reason: body.reason,
      status: "requested",
      actor: "demo.planner",
      created_at: new Date().toISOString(),
    };
    list.push(created);
    return HttpResponse.json(created, { status: 201 });
  }),
  // Advance a supplement's approval trail. Approving the pending step promotes
  // the next step to pending; approving the LAST step flips the supplement to
  // `approved` and rolls its amount into the plan's authorized_total. Rejecting
  // marks the pending step + the supplement rejected (no total change).
  http.patch(`${P}/v1/supplements/:sid`, async ({ params, request }) => {
    const sid = params.sid as string;
    const body = (await request.json()) as { status?: "approved" | "rejected" };
    let found: SupplementOut | undefined;
    let planId: string | undefined;
    for (const [pid, list] of Object.entries(supplementsByPlan)) {
      const s = list.find((x) => x.id === sid);
      if (s) {
        found = s;
        planId = pid;
        break;
      }
    }
    if (!found || !planId) return new HttpResponse(null, { status: 404 });

    const steps = found.approvals ?? [];
    const idx = steps.findIndex((st) => st.status === "pending");
    const pending = idx >= 0 ? steps[idx] : undefined;
    const nowIso = new Date().toISOString();
    if (body.status === "rejected") {
      if (pending) steps[idx] = { ...pending, status: "rejected", decided_at: nowIso };
      found.status = "rejected";
    } else if (body.status === "approved") {
      if (pending) {
        steps[idx] = { ...pending, status: "approved", decided_at: nowIso };
        const nextLocked = steps.findIndex((st) => st.status === "locked");
        const locked = nextLocked >= 0 ? steps[nextLocked] : undefined;
        if (locked) {
          steps[nextLocked] = { ...locked, status: "pending" };
        } else {
          // No further steps — the supplement is fully approved.
          found.status = "approved";
        }
      } else {
        found.status = "approved";
      }
    }
    // When a supplement becomes approved, add its amount to the plan total.
    if (found.status === "approved") {
      const plan = planById[planId];
      if (plan) plan.authorized_total = (plan.authorized_total ?? 0) + found.amount;
    }
    return HttpResponse.json(found);
  }),

  // ── Plan datasets ──
  http.get(`${P}/v1/plans/:id/datasets`, ({ params }) =>
    HttpResponse.json(getDatasets(params.id as string)),
  ),
  http.put(`${P}/v1/plans/:id/programa`, async ({ params, request }) => {
    const body = (await request.json()) as { rows: ProgramaStep[] };
    getDatasets(params.id as string).programa = body.rows;
    return HttpResponse.json(body.rows);
  }),
  http.put(`${P}/v1/plans/:id/hole-plan`, async ({ params, request }) => {
    const body = (await request.json()) as { rows: HolePlanRow[] };
    getDatasets(params.id as string).hole_plan = body.rows;
    return HttpResponse.json(body.rows);
  }),
  http.put(`${P}/v1/plans/:id/casing`, async ({ params, request }) => {
    const body = (await request.json()) as { rows: CasingRow[] };
    getDatasets(params.id as string).casing = body.rows;
    return HttpResponse.json(body.rows);
  }),
  http.put(`${P}/v1/plans/:id/objetivo`, async ({ params, request }) => {
    const body = (await request.json()) as { rows: AttributeRow[] };
    getDatasets(params.id as string).objetivo = body.rows;
    return HttpResponse.json(body.rows);
  }),
  http.put(`${P}/v1/plans/:id/atributos`, async ({ params, request }) => {
    const body = (await request.json()) as { rows: AttributeRow[] };
    getDatasets(params.id as string).atributos = body.rows;
    return HttpResponse.json(body.rows);
  }),
  http.put(`${P}/v1/plans/:id/afe-lines`, async ({ params, request }) => {
    const body = (await request.json()) as { rows: AfeLineRow[] };
    getDatasets(params.id as string).afe_lines = body.rows;
    return HttpResponse.json(body.rows);
  }),
  http.put(`${P}/v1/plans/:id/trajectory`, async ({ params, request }) => {
    const body = (await request.json()) as { stations: TrajectoryStationInput[] };
    const computed = computeTrajectory(body.stations);
    getDatasets(params.id as string).trajectory = computed;
    return HttpResponse.json(computed);
  }),

  // ── Plan timeline (W7) — flat node contract ──
  http.get(`${P}/v1/plans/:id/plan-timeline`, ({ params }) =>
    HttpResponse.json(getTimeline(params.id as string)),
  ),
  // W PUTs the plan nodes; we keep the server's actual/npt overlays authoritative
  // and merge the incoming plan-kind nodes over them.
  http.put(`${P}/v1/plans/:id/plan-timeline`, async ({ params, request }) => {
    const planId = params.id as string;
    const body = (await request.json()) as { nodes: TimelineNode[] };
    const tl = getTimeline(planId);
    const overlays = tl.nodes.filter((n) => n.kind !== "plan");
    const plans = (body.nodes ?? []).filter((n) => n.kind === "plan");
    tl.nodes = [...overlays, ...plans];
    return HttpResponse.json(tl);
  }),

  // ── Publish (W→Z + W→Aura) ──
  http.get(`${P}/v1/plans/:id/publish-preview`, ({ params }) => {
    const planId = params.id as string;
    const p = planById[planId];
    if (!p) return new HttpResponse(null, { status: 404 });
    const ds = getDatasets(planId);
    return HttpResponse.json(buildPublishPreview(planId, p, ds));
  }),
  http.post(`${P}/v1/plans/:id/publish`, ({ params }) => {
    const planId = params.id as string;
    const p = planById[planId];
    if (!p) return new HttpResponse(null, { status: 404 });
    p.status = "published";
    p.published_at = new Date().toISOString();
    const resp: PublishResultOut = {
      plan_id: planId,
      version: p.version,
      published_at: p.published_at,
      z_baseline: true,
      aura_emitted: true,
      aura_data_types: [
        "afe",
        "programa",
        "plan_costos",
        "hole_plan",
        "casing",
        "atributos",
        "secuencia_operativa",
      ],
    };
    return HttpResponse.json(resp);
  }),
];
